/**
 * DefaultMediaPage — web-only full-screen viewer for media on the cloud default
 * storage bucket, at `default-media/:id`.
 *
 * The media lives on the cloud default storage bucket (authenticated, but not
 * encrypted) — it is not "public", hence the `default-media` (not the old
 * `public-content`) route naming. Distinct from the chat-media viewer
 * (`MediaDetailsPage` at `chat/media/:id`), which renders client-E2EE chat media.
 *
 * Web replacement for the destination of Flutter's `_onScreenshotTapped`
 * (`lib/presentation/screens/feedback/user_feedback_details_screen.dart:48-59`),
 * which pushes the content-list screen (`navigateToContentListScreen`). On the
 * web the feedback-details screenshot tap navigates here instead, passing the
 * screenshot `url` via `location.state`.
 *
 * Route contract:
 * - The `:id` path segment is a random id minted by
 *   `Navigator.navigateToDefaultMedia` purely so the route cache treats
 *   every visit as a fresh instance — it is NOT read for data.
 * - The real payload is `location.state.url` (nullable) plus an optional
 *   `location.state.aspectRatio` (width / height). When the url is present, the
 *   page renders a `ContentThumbnail` in an aspect-ratio-aware box capped at
 *   `85vh` max height (matching `VideoComponent`'s portrait `85vh` ceiling);
 *   when null/undefined it renders a centered error message. A missing aspect
 *   ratio falls back to `VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO`, the same default
 *   the feedback grid uses.
 *
 * App bar: the shared `ExpauseAppBar`
 * (`presentation/components/common/appBar/ExpauseAppBar.tsx`) with **no**
 * `title` — Flutter's media viewer renders a title-less bar
 * (`content_list_widget.dart:1112-1147`, `title: Container()` at `:1126`), and
 * its back chevron maps to `_onBackPress` → `NavigationService.pop()`
 * (`user_feedback_details_screen.dart:44`), which the web renders as the
 * deep-link-guarded `Navigator.goBackOr` (`presentation.md` → App bar → "Back
 * affordance") — a superset of that `pop()`, identical whenever there is in-app
 * history. Moving off the hand-rolled row makes two intentional
 * visual changes: the bar becomes sticky with a blurred 50%-opacity backdrop,
 * and `responsive.safeArea.top` is now honoured. `leadingQa` keeps the page's
 * existing `data-qa-id="default-media-back"`.
 *
 * Video playback is intentionally out of scope for this task (see the
 * `// TODO: @claude` marker at the render branch).
 */
import { useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import {
    useResponsiveDesign,
    useIsLandscape,
    isMobileViewport,
} from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import { ExpauseAppBar } from '@/presentation/components/common/appBar/ExpauseAppBar';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { ContentThumbnail } from '@/presentation/feature/content/components/ContentThumbnail';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface DefaultMediaLocationState {
    /** The screenshot URL to display full-viewport (nullable). */
    url?: string | null;
    /** Screenshot aspect ratio (width / height) for box sizing. */
    aspectRatio?: number;
}

/**
 * Compute the aspect-ratio + max-dimension box for the full-page thumbnail.
 *
 * Mirrors `VideoComponent.getVideoWrapperStyle` (around
 * `VideoComponent.tsx:326-351`): branch on the media's landscape-ness and the
 * viewport's orientation/size to pick the box aspect ratio, then — for the
 * height-capped portrait branch — derive a matching `maxWidth` from the `85vh`
 * ceiling so the box keeps its ratio (no distortion) while never overflowing.
 *
 * The image inside stays `object-cover` (Flutter parity — the screenshot grid
 * uses a cover fit); because the *box* carries the constraint and keeps its
 * aspect ratio, the cover image fills it without stretching or cropping.
 */
const getThumbnailBoxStyle = (
    aspectRatio: number,
    deviceIsMobile: boolean,
    deviceIsLandscape: boolean,
): React.CSSProperties => {
    const mediaIsLandscape = aspectRatio > 1.0;
    if (mediaIsLandscape) {
        // Landscape media: use the media's own ratio on a mobile portrait
        // viewport (where vertical room is tight), else cap at 21:9 so a wide
        // screenshot never produces an overly tall box on desktop/landscape —
        // matching VideoComponent's landscape branch (full width, no max
        // width).
        const useMediaRatio = deviceIsMobile && !deviceIsLandscape;
        return {
            width: '100%',
            maxWidth: '100%',
            aspectRatio: useMediaRatio
                ? aspectRatio.toString()
                : CONTENT_CONSTANTS.feedbackLandscapeAspectRatio,
            margin: '0 auto',
        };
    }
    // Portrait media: cap height at the viewport (`85vh`) and derive the max
    // width from the box ratio (`width = 85vh * ratio`) — VideoComponent's
    // portrait branch.
    const portraitRatio = deviceIsMobile
        ? CONTENT_CONSTANTS.feedbackAspectRatioValue
        : aspectRatio;
    return {
        width: '100%',
        maxWidth: `calc(${CONTENT_CONSTANTS.defaultMediaMaxHeight} * ${portraitRatio})`,
        aspectRatio: portraitRatio.toString(),
        maxHeight: CONTENT_CONSTANTS.defaultMediaMaxHeight,
        margin: '0 auto',
    };
};

export const DefaultMediaPage = () => {
    const { theme } = useTheme();
    const responsive = useResponsiveDesign();
    const { l10n } = useLocale();
    const location = useLocation();
    // Reactive viewport orientation/size — re-renders on resize so the box
    // (landscape vs portrait branch) recomputes, like VideoComponent.
    const deviceIsLandscape = useIsLandscape();
    const deviceIsMobile = isMobileViewport();

    // `:id` is the random cache-busting segment minted by the Navigator. It is
    // intentionally NOT read for data — the real payload is `location.state.url`.
    useParams<{ id: string }>();

    const locationState = location.state as DefaultMediaLocationState | undefined;
    const url = locationState?.url ?? null;
    // Same default the grid uses (`buildScreenshotMedia`) so a missing ratio
    // falls back consistently.
    const aspectRatio =
        locationState?.aspectRatio ?? VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO;

    // Canonical screen-key wiring (mirrors `ContentDetailsPage` /
    // `SupportItemPage`). Capture once on mount so the `KeepAliveOutlet` can
    // disambiguate two pushed instances of the same detail route.
    const { currentScreenKeyRef } = useKeepAlive();
    const screenKeyRef = useRef<string>(
        currentScreenKeyRef.current || generateScreenKey(location.pathname),
    );
    const screenKey = screenKeyRef.current;

    useBasePage({ screenKey, onInit: () => { } });

    return (
        <div
            {...qaAttr({ id: 'default-media', status: 'ready' })}
            style={{
                minHeight: '100vh',
                backgroundColor: theme.background,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Title-less app bar (Flutter `title: Container()`,
                content_list_widget.dart:1126). Back mirrors Flutter's
                `_onBackPress` -> `NavigationService.pop()`
                (user_feedback_details_screen.dart:44), deep-link-guarded.
                Fallback is the Feed: the sole in-app pusher is the feedback
                screenshot tap, and reaching THAT parent needs the feedback
                entity, which only travels in the `location.state` a deep link
                does not have — so the home shell is the only reachable ancestor. */}
            <ExpauseAppBar
                rootQa={{ id: 'default-media-app-bar' }}
                leadingQa={{ id: 'default-media-back' }}
                onLeadingTap={() => Navigator.goBackOr(() => Navigator.navigateToFeed())}
            />

            {/* Body — full-viewport, centered. */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: `${responsive.spacing.lg}px`,
                }}
            >
                {url ? (
                    // TODO: @claude add a follow up task for this: video playback in DefaultMediaPage — Flutter lib/presentation/screens/feedback/user_feedback_details_screen.dart:48-59
                    // Aspect-ratio-aware box capped at `85vh` max height with a
                    // max width derived from the media ratio, landscape-aware
                    // (media + viewport) — mirrors VideoComponent's max-dimension
                    // approach. Keeps the box's ratio so the cover image fills it
                    // without distortion.
                    <div
                        style={getThumbnailBoxStyle(
                            aspectRatio,
                            deviceIsMobile,
                            deviceIsLandscape,
                        )}
                    >
                        <ContentThumbnail
                            thumbnailUrl={url}
                            qa={{ id: 'default-media-thumbnail' }}
                        />
                    </div>
                ) : (
                    <p
                        {...qaAttr({ id: 'default-media-error' })}
                        style={{
                            margin: 0,
                            textAlign: 'center',
                            color: theme.text.secondary,
                            fontSize: responsive.font.base,
                        }}
                    >
                        {l10n.defaultMedia.errorNoMedia}
                    </p>
                )}
            </div>
        </div>
    );
};
